import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Person } from '@domain/entities/Person';
import {
  BLOOD_TYPES,
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABEL,
  MARITAL_STATUSES,
  MARITAL_STATUS_LABEL,
  recordCompleteness,
  type PersonalRecord,
} from '@domain/entities/PersonalRecord';
import {
  PHONE_NUMBER_LENGTH,
  formatStoredPhone,
  phoneDigitsOf,
} from '@domain/value-objects/PhoneNumber';
import { Banner, Button, Card, Chip, Field, inputClass, selectClass } from '@presentation/design-system';
import { reasonFor } from '@presentation/format';
import { maskDraftPhone } from './maskDraftPhone';
import { useAccess } from '@presentation/hooks/useAccess';
import { useSensitiveAccess } from '@presentation/providers/SensitiveAccessProvider';
import { useToast } from '@presentation/providers/ToastProvider';
import { getServices } from '@infrastructure/services';
import styles from './PersonalRecordCard.module.css';

/**
 * El expediente personal, en la ficha.
 *
 * Lo que RR.HH. recogió en el formulario de la familia Knotion y no cabe en la
 * ficha de trabajo: estado civil, INFONAVIT, estudios, tipo de sangre, alergias y
 * los datos de contacto que no son los de la empresa.
 *
 * **Sin `ver_expediente` la tarjeta no existe**, ni vacía, por lo mismo que la
 * compensación: un recuadro que diga «no tienes acceso» confirma que hay un
 * expediente y dónde vive. Y `ver_expediente` no lo tiene un líder a propósito —
 * dirigir a alguien no incluye saber su estado civil.
 *
 * **Y vive en dos sitios**: aquí como tarjeta con su título, y en el perfil como
 * una de sus pestañas —Avisos, Expediente, Emergencia—, sin marco. Lo elige
 * `frame`.
 *
 * En la ficha la columna mide unos 425 px por dentro a 1280 —y eso medía también
 * en el perfil, hasta que el 7 oct 2026 su panel pasó a ocupar el ancho entero:
 * ahí son cuatro pistas y dos filas, 305 px—. Por la ficha la pista de la rejilla
 * es de 11 rem y no de 15: con 15 no caben dos —240 + 240 + 20 de
 * hueco son 500— y los ocho campos caían en una sola columna, 709 px de alto en
 * el perfil y 700 y pico en la ficha. Con 11 rem entran dos pistas de 203 px y el
 * panel baja a 465. Medido, no deducido: **esta nota decía «a todo el ancho y
 * debajo de las otras dos», y llevaba tiempo sin ser verdad** — la tarjeta nunca
 * ocupó el ancho entero, siempre estuvo en una de las dos columnas de la ficha.
 *
 * ## Por qué se ve aunque esté vacío
 *
 * De 337 personas contestaron 326, y ninguna contestó todo: el tipo de sangre lo
 * trae el 81 %, las alergias el 75 %. Un hueco es lo normal, así que se enseña
 * «Sin registrar» en vez de esconder el campo — y arriba, cuánto falta. Esconder
 * lo vacío haría imposible saber a quién hay que preguntarle.
 */

interface PersonalRecordCardProps {
  readonly person: Person;
  /**
   * Si quien mira es su dueño.
   *
   * Cambia dos cosas: se ve sin `ver_expediente` —ese permiso es sobre el
   * expediente ajeno— y el estado vacío habla en segunda persona. Lo que no
   * cambia es quién puede editar.
   */
  readonly ownProfile?: boolean;
  /**
   * Cómo se enmarca.
   *
   * `card` es su propia tarjeta con título, que es lo que necesita en la ficha de
   * RR.HH. — ahí convive con la compensación y los documentos, y cada una tiene
   * que decir qué es. `plain` la deja sin marco ni título para meterla dentro de
   * un panel que ya pone los dos: en el perfil es una pestaña, entre Avisos y
   * Emergencia, y una tarjeta dentro del panel dibujaría dos bordes y
   * repetiría en el título lo que ya dice la pestaña.
   */
  readonly frame?: 'card' | 'plain';
}

/** Lo que el formulario acepta, antes de convertirse en expediente. */
interface Draft {
  maritalStatus: string;
  hasInfonavitCredit: string;
  educationLevel: string;
  bloodType: string;
  children: string;
  allergies: string;
  personalEmail: string;
  personalPhone: string;
}

const draftFrom = (record: PersonalRecord | null): Draft => ({
  maritalStatus: record?.maritalStatus ?? '',
  hasInfonavitCredit:
    record?.hasInfonavitCredit === true ? 'si' : record?.hasInfonavitCredit === false ? 'no' : '',
  educationLevel: record?.educationLevel ?? '',
  bloodType: record?.bloodType ?? '',
  children: record?.children === null || record === null ? '' : String(record.children),
  allergies: record?.allergies ?? '',
  personalEmail: record?.personalEmail ?? '',
  /*
   * Crudo, a propósito.
   *
   * Normalizarlo aquí parecía lo ordenado, y le costaba dígitos a catorce
   * expedientes: en producción hay teléfonos de once y doce cifras
   * —«554435074820», «44433930445»— y recortarlos a diez al abrir el formulario
   * los dejaba mutilados en cuanto alguien guardara, sin haber tocado el campo.
   * Lo que nadie editó se guarda como estaba.
   */
  personalPhone: record?.personalPhone ?? '',
});

export const PersonalRecordCard = ({
  person,
  ownProfile = false,
  frame = 'card',
}: PersonalRecordCardProps): ReactNode => {
  const { holds } = useAccess();
  const toast = useToast();
  /*
   * El expediente propio se ve sin permiso, porque no hace falta.
   *
   * `ver_expediente` gobierna el expediente **de otra persona** — por eso no lo
   * tiene un líder. Sobre el suyo, la regla de Firestore ya deja leer a su dueño
   * (`isSelf`), y era la pantalla la que no se lo enseñaba: el portal le pedía
   * estos datos, los guardaba, y después no había forma de que los viera.
   *
   * Editar sigue pidiendo permiso: `savePersonalRecord` exige `editar_expediente`,
   * así que para su dueño la tarjeta es de lectura. Un botón de corregir aquí
   * sería una vía muerta con un error de permisos al final.
   */
  const canView = ownProfile || holds('ver_expediente');
  const canEdit = holds('editar_expediente');

  /*
   * El expediente **ajeno** pide volver a identificarse; el propio no.
   *
   * Nadie tiene que demostrar que es él para mirarse a sí mismo, y pedirlo
   * enseñaría que el portal no distingue entre consultar y consultarse. Sobre el
   * de otra persona sí: aquí están su estado civil, sus alergias y su teléfono
   * particular, y una sesión abierta sólo prueba que alguien se identificó alguna
   * vez. Ver `SensitiveAccessProvider`.
   */
  const sensitive = useSensitiveAccess();
  const abierto = ownProfile || sensitive.granted;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(draftFrom(null));
  const [busy, setBusy] = useState(false);

  const record = useQuery({
    queryKey: ['personDetail', 'personalRecord', person.id],
    /* No se pide hasta que esté autorizado: la lectura no se hace y luego se
       esconde, no se hace. */
    enabled: canView && abierto,
    queryFn: async () => {
      const result = await getServices().personalRecords.getFor(person.id);
      if (!result.ok) throw new Error(reasonFor(result.error));
      return result.value;
    },
  });

  /*
   * El borrador se rellena cuando llega la respuesta, no al montar.
   *
   * Es el mismo tropiezo que ya mordió en la baja: un valor inicial de `useState`
   * lo lee React una sola vez, y esta tarjeta se monta con la ficha, cuando la
   * consulta todavía no ha vuelto. El formulario abría en blanco sobre un
   * expediente que sí tenía datos, y guardarlo los habría borrado.
   */
  useEffect(() => {
    if (!editing) setDraft(draftFrom(record.data ?? null));
  }, [record.data, editing]);

  if (!canView) return null;

  if (!abierto) {
    return (
      <Marco frame={frame}>
        <div className={styles.body}>
          <Banner tone="info">
            Aquí están los datos personales de {person.displayName}. Vuelve a identificarte
            para verlos.
          </Banner>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={() => {
                void sensitive.ensure();
              }}
            >
              Confirmar mi identidad
            </Button>
          </div>
        </div>
      </Marco>
    );
  }

  const actual = record.data ?? null;
  const faltan = Math.round((1 - recordCompleteness(actual)) * 8);

  const save = async (): Promise<void> => {
    setBusy(true);
    const result = await getServices().personalRecords.save({
      personId: person.id,
      maritalStatus: draft.maritalStatus.length > 0 ? draft.maritalStatus : null,
      hasInfonavitCredit:
        draft.hasInfonavitCredit === 'si' ? true : draft.hasInfonavitCredit === 'no' ? false : null,
      educationLevel: draft.educationLevel.length > 0 ? draft.educationLevel : null,
      bloodType: draft.bloodType.length > 0 ? draft.bloodType : null,
      children: draft.children.length > 0 ? Number(draft.children) : null,
      allergies: draft.allergies.trim(),
      personalEmail: draft.personalEmail.trim(),
      personalPhone: draft.personalPhone.trim(),
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudo guardar el expediente.${reasonFor(result.error)}`);
      return;
    }
    toast.ok('Expediente actualizado.');
    setEditing(false);
    await record.refetch();
  };

  return (
    <Marco frame={frame}>
      {record.isPending ? (
        <p className={styles.note}>Cargando…</p>
      ) : editing ? (
        <div className={styles.body}>
          <div className={styles.grid}>
            <Field label="Estado civil">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={draft.maritalStatus}
                  onChange={(event) => {
                    setDraft({ ...draft, maritalStatus: event.target.value });
                  }}
                >
                  <option value="">Sin registrar</option>
                  {MARITAL_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {MARITAL_STATUS_LABEL[value]}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Crédito INFONAVIT">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={draft.hasInfonavitCredit}
                  onChange={(event) => {
                    setDraft({ ...draft, hasInfonavitCredit: event.target.value });
                  }}
                >
                  <option value="">Sin registrar</option>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              )}
            </Field>

            {/* A lo ancho: ver `.wide`. Sus opciones no caben en media fila. */}
            <div className={styles.wide}>
              <Field label="Último grado de estudios">
                {(id) => (
                  <select
                    id={id}
                    className={selectClass()}
                    value={draft.educationLevel}
                    onChange={(event) => {
                      setDraft({ ...draft, educationLevel: event.target.value });
                    }}
                  >
                    <option value="">Sin registrar</option>
                    {EDUCATION_LEVELS.map((value) => (
                      <option key={value} value={value}>
                        {EDUCATION_LEVEL_LABEL[value]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>

            <Field label="Tipo de sangre">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={draft.bloodType}
                  onChange={(event) => {
                    setDraft({ ...draft, bloodType: event.target.value });
                  }}
                >
                  <option value="">Sin registrar</option>
                  {BLOOD_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Hijos" hint="Vacío no es lo mismo que cero.">
              {(id) => (
                <input
                  id={id}
                  type="number"
                  min={0}
                  max={20}
                  className={inputClass()}
                  placeholder="Sin registrar"
                  value={draft.children}
                  onChange={(event) => {
                    setDraft({ ...draft, children: event.target.value });
                  }}
                />
              )}
            </Field>

            <Field label="Correo personal" hint="El de fuera de la empresa.">
              {(id) => (
                <input
                  id={id}
                  type="email"
                  className={inputClass()}
                  placeholder="alguien@gmail.com"
                  value={draft.personalEmail}
                  onChange={(event) => {
                    setDraft({ ...draft, personalEmail: event.target.value });
                  }}
                />
              )}
            </Field>

            {/*
              El borrador guarda **dígitos**, no lo que se ve: el campo pinta
              `formatPhoneDigits` y devuelve dígitos al escribir. Es el mismo
              patrón que los contactos de emergencia del perfil, y por el mismo
              motivo — guardar la cadena con espacios obligaría a limpiarla en cada
              lectura y dejaría entrar formato a la base.
            */}
            <Field label="Teléfono personal">
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  inputMode="tel"
                  placeholder="10 dígitos con lada"
                  value={maskDraftPhone(draft.personalPhone)}
                  onChange={(event) => {
                    setDraft({
                      ...draft,
                      // Normalizar primero y cortar después, nunca al revés:
                      // `phoneDigitsOf` es quien se come el «+52» y el «1» de
                      // «+52 1 55 …», y cortar antes dejaría diez dígitos que
                      // empiezan por la lada del país. El tope existe porque sin
                      // él el campo acepta lo que sea —23 dígitos, y se guardan—.
                      personalPhone: phoneDigitsOf(event.target.value).slice(
                        0,
                        PHONE_NUMBER_LENGTH,
                      ),
                    });
                  }}
                />
              )}
            </Field>
          </div>

          {/* A lo ancho: las respuestas reales van de «penicilina» a una frase. */}
          <Field label="Alergias" hint="Tal como lo contó. «Ninguna» también es una respuesta.">
            {(id) => (
              <input
                id={id}
                className={inputClass()}
                placeholder="Ninguna"
                value={draft.allergies}
                onChange={(event) => {
                  setDraft({ ...draft, allergies: event.target.value });
                }}
              />
            )}
          </Field>

          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                void save();
              }}
            >
              {busy ? 'Guardando…' : 'Guardar expediente'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setDraft(draftFrom(actual));
              }}
            >
              Descartar
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.body}>
          {actual === null ? (
            <Banner tone="info">
              {ownProfile
                ? 'Todavía no tenemos estos datos tuyos. Los registra Recursos Humanos; si te faltan, avísales.'
                : `Todavía no contestó el formulario de datos.${canEdit ? ' Puedes registrarlo aquí.' : ''}`}
            </Banner>
          ) : (
            faltan > 0 && (
              <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
                {faltan === 1 ? 'Falta 1 dato' : `Faltan ${String(faltan)} datos`}
              </Chip>
            )
          )}

          <dl className={styles.grid}>
            <Dato
              etiqueta="Estado civil"
              valor={
                actual === null || actual.maritalStatus === null
                  ? null
                  : MARITAL_STATUS_LABEL[actual.maritalStatus]
              }
            />
            <Dato
              etiqueta="Crédito INFONAVIT"
              valor={actual?.hasInfonavitCredit === true ? 'Sí' : actual?.hasInfonavitCredit === false ? 'No' : null}
            />
            <Dato
              etiqueta="Último grado de estudios"
              valor={
                actual === null || actual.educationLevel === null
                  ? null
                  : EDUCATION_LEVEL_LABEL[actual.educationLevel]
              }
            />
            <Dato etiqueta="Tipo de sangre" valor={actual?.bloodType ?? null} />
            <Dato
              etiqueta="Hijos"
              valor={actual === null || actual.children === null ? null : String(actual.children)}
            />
            <Dato etiqueta="Correo personal" valor={emptyToNull(actual?.personalEmail)} />
            {/* Agrupado como se escribe en México. `formatStoredPhone` devuelve el
                original cuando no puede: lo que se cargó del Excel trae de todo, y
                reescribir algo que no es un teléfono lo escondería. */}
            <Dato
              etiqueta="Teléfono personal"
              valor={
                actual === null || actual.personalPhone.length === 0
                  ? null
                  : formatStoredPhone(actual.personalPhone)
              }
            />
            <Dato etiqueta="Alergias" valor={emptyToNull(actual?.allergies)} />
          </dl>

          {canEdit && (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setDraft(draftFrom(actual));
                  setEditing(true);
                }}
              >
                {actual === null ? 'Registrar expediente' : 'Corregir expediente'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Marco>
  );
};

/**
 * La tarjeta, o ningún marco.
 *
 * Componente de primer nivel y no una función declarada dentro del render:
 * definida dentro, React ve un tipo de componente nuevo en cada pintada y
 * desmonta lo que cuelga de él — el formulario perdería el foco en cada tecla.
 */
const Marco = ({
  frame,
  children,
}: {
  readonly frame: 'card' | 'plain';
  readonly children: ReactNode;
}): ReactNode =>
  frame === 'plain' ? <>{children}</> : <Card title="Expediente personal">{children}</Card>;

const emptyToNull = (value: string | undefined): string | null =>
  value === undefined || value.trim().length === 0 ? null : value;

/**
 * Un dato del expediente. Los huecos se enseñan, no se esconden.
 *
 * Un campo que desaparece cuando está vacío deja al que lo lee sin saber si la
 * persona contestó «ninguna» o no contestó nada — y de las dos, sólo una da
 * trabajo pendiente.
 */
const Dato = ({
  etiqueta,
  valor,
}: {
  readonly etiqueta: string;
  readonly valor: string | null;
}): ReactNode => (
  <div className={styles.dato}>
    <dt className={styles.datoKey}>{etiqueta}</dt>
    <dd className={valor === null ? styles.datoVacio : styles.datoValor}>
      {valor ?? 'Sin registrar'}
    </dd>
  </div>
);
